'use client';

import { useState, useEffect } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface Capability {
  id: number;
  title: string;
  description: string;
  icon: string;
  metric: string;
  metricLabel: string;
}

interface WarehouseCapabilitiesProps {
  className?: string;
}

const WarehouseCapabilities = ({ className = '' }: WarehouseCapabilitiesProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeTab, setActiveTab] = useState<number>(1);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const capabilities: Capability[] = [
  {
    id: 1,
    title: 'Climate-Controlled Storage',
    description: 'State-of-the-art temperature and humidity control systems maintain optimal conditions for sensitive medical equipment, ensuring product integrity from storage to delivery.',
    icon: 'CloudIcon',
    metric: '24/7',
    metricLabel: 'Environmental Monitoring'
  },
  {
    id: 2,
    title: 'Advanced Inventory Management',
    description: 'Real-time tracking systems with barcode and RFID technology provide complete visibility of equipment location, status, and compliance documentation throughout the supply chain.',
    icon: 'QrCodeIcon',
    metric: '99.9%',
    metricLabel: 'Inventory Accuracy'
  },
  {
    id: 3,
    title: 'Quality Control Protocols',
    description: 'Multi-stage inspection processes verify equipment condition, functionality, and compliance documentation before shipment, with detailed quality assurance records maintained for every item.',
    icon: 'ClipboardDocumentCheckIcon',
    metric: '5-Point',
    metricLabel: 'Inspection Process'
  },
  {
    id: 4,
    title: 'Secure Equipment Handling',
    description: 'Specialized handling equipment and trained personnel ensure safe movement of delicate medical devices, with dedicated zones for different equipment categories and sensitivity levels.',
    icon: 'ShieldCheckIcon',
    metric: '100%',
    metricLabel: 'Damage-Free Rate'
  }];


  if (!isHydrated) {
    return (
      <section className={`py-20 bg-surface ${className}`}>
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="h-96 bg-white rounded-2xl animate-pulse"></div>
        </div>
      </section>);

  }

  return (
    <section className={`py-20 bg-surface ${className}`}>
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-block px-4 py-2 bg-accent/10 text-accent rounded-full text-sm font-semibold mb-4">
            Infrastructure
          </div>
          <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4">
            Warehouse & Logistics Capabilities
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Our advanced warehouse facilities combine cutting-edge technology with rigorous quality control processes to ensure your medical equipment arrives in perfect condition, on time, every time.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div className="relative rounded-2xl overflow-hidden shadow-brand-lg">
            <AppImage
              src="https://images.unsplash.com/photo-1549194388-2469d59ec75c"
              alt="Modern warehouse interior with organized medical equipment storage racks, climate control systems, and professional logistics staff managing inventory"
              className="w-full h-[500px] object-cover" />

            <div className="absolute inset-0 bg-gradient-to-t from-primary/60 to-transparent"></div>
            <div className="absolute bottom-8 left-8 right-8">
              <div className="bg-white/95 backdrop-blur-sm rounded-xl p-6 shadow-brand">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-primary mb-1">150K+</p>
                    <p className="text-xs text-text-secondary">Sq Ft Facility</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-accent mb-1">$50M+</p>
                    <p className="text-xs text-text-secondary">Inventory Value</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-brand-accent mb-1">48hr</p>
                    <p className="text-xs text-text-secondary">Avg Fulfillment</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="flex space-x-2 border-b border-border pb-4">
              {capabilities.map((cap) =>
              <button
                key={cap.id}
                onClick={() => setActiveTab(cap.id)}
                className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all duration-300 ${
                activeTab === cap.id ?
                'bg-primary text-white shadow-brand' :
                'text-text-secondary hover:bg-surface'}`
                }>

                  {cap.id}
                </button>
              )}
            </div>

            {capabilities.map(
              (cap) =>
              activeTab === cap.id &&
              <div key={cap.id} className="animate-fade-in">
                    <div className="flex items-start space-x-4 mb-6">
                      <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Icon name={cap.icon as any} size={28} className="text-primary" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold text-text-primary mb-2">{cap.title}</h3>
                        <p className="text-text-secondary leading-relaxed">{cap.description}</p>
                      </div>
                    </div>
                    <div className="bg-white rounded-xl p-6 shadow-brand border border-border">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-text-secondary mb-1">{cap.metricLabel}</p>
                          <p className="text-3xl font-bold text-primary">{cap.metric}</p>
                        </div>
                        <Icon name="ChartBarIcon" size={48} className="text-primary/20" />
                      </div>
                    </div>
                  </div>

            )}
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white rounded-xl p-6 shadow-brand border border-border">
            <Icon name="TruckIcon" size={32} className="text-primary mb-4" />
            <h4 className="font-bold text-text-primary mb-2">Express Shipping</h4>
            <p className="text-sm text-text-secondary">
              Expedited delivery options for urgent equipment needs with real-time tracking
            </p>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-brand border border-border">
            <Icon name="CubeIcon" size={32} className="text-accent mb-4" />
            <h4 className="font-bold text-text-primary mb-2">Custom Packaging</h4>
            <p className="text-sm text-text-secondary">
              Specialized packaging solutions designed for delicate medical equipment protection
            </p>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-brand border border-border">
            <Icon name="DocumentTextIcon" size={32} className="text-brand-accent mb-4" />
            <h4 className="font-bold text-text-primary mb-2">Documentation</h4>
            <p className="text-sm text-text-secondary">
              Complete compliance documentation and certificates included with every shipment
            </p>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-brand border border-border">
            <Icon name="ClockIcon" size={32} className="text-brand-cta mb-4" />
            <h4 className="font-bold text-text-primary mb-2">24/7 Operations</h4>
            <p className="text-sm text-text-secondary">
              Round-the-clock warehouse operations ensuring continuous order fulfillment
            </p>
          </div>
        </div>
      </div>
    </section>);

};

export default WarehouseCapabilities;